/**
 * Scroll reveal, part 2 of 2 (part 1 is snippets/asd-reveal-head.liquid, which hides the targets from the first
 * paint and shares the selectors as window.__asdReveal).
 *
 * As targets come into view they fade and rise into place with an ease-out curve. Targets that arrive together
 * are staggered top-to-bottom. Targets that are already above the viewport (a restored scroll position, a fast
 * scroll) are shown instantly, and anything the page adds later (filtering, quick add, section re-renders) is shown
 * immediately so nothing can stay hidden.
 */
(() => {
  const root = document.documentElement;
  const config = window.__asdReveal;
  if (!config || !root.classList.contains('asd-reveal')) return;

  // We're running: cancel the head script's "show everything" safety net.
  clearTimeout(window.__asdRevealTimer);

  const STEP_MS = 90; // gap between items revealed together
  const MAX_DELAY_MS = 540;
  const SETTLE_MS = 1400; // how long to keep the transition classes (longest transition + delay headroom)

  const selector = `${config.items}, ${config.sections}`;
  const outermostSelector = config.items;

  /** @type {Set<Element>} targets waiting to be revealed */
  const pending = new Set();
  /** @type {WeakSet<Element>} targets we already know about */
  const seen = new WeakSet();
  /** @type {Element[]} */
  let queue = [];
  let flushScheduled = false;

  const observer = new IntersectionObserver(onIntersect, { rootMargin: '0px 0px -7% 0px', threshold: 0 });

  /** @param {Element} el */
  function isTarget(el) {
    // A card animates as one unit: skip targets nested inside another target.
    const parent = el.parentElement;
    return !(parent && parent.closest(outermostSelector));
  }

  /** @param {ParentNode} scope */
  function collect(scope) {
    const found = [];
    if (scope instanceof Element && scope.matches(selector)) found.push(scope);
    scope.querySelectorAll(selector).forEach((el) => found.push(el));
    return found.filter((el) => (el.matches(config.sections) ? true : isTarget(el)));
  }

  /** @param {Element} el @param {number} delay */
  function reveal(el, delay) {
    pending.delete(el);
    observer.unobserve(el);
    el.style.setProperty('--asd-delay', `${delay}ms`);
    el.classList.add('asd-in');
    setTimeout(() => {
      el.classList.remove('asd-in');
      el.classList.add('asd-done');
      el.style.removeProperty('--asd-delay');
    }, delay + SETTLE_MS);
  }

  /** @param {Element} el */
  function showNow(el) {
    pending.delete(el);
    observer.unobserve(el);
    el.classList.add('asd-in', 'asd-in-instant');
    // Let the browser paint the visible state, then hand the element back to its own styles.
    requestAnimationFrame(() => {
      el.classList.remove('asd-in', 'asd-in-instant');
      el.classList.add('asd-done');
    });
  }

  function flush() {
    flushScheduled = false;
    const batch = queue.filter((el) => pending.has(el));
    queue = [];
    batch.sort((a, b) => {
      const ra = a.getBoundingClientRect();
      const rb = b.getBoundingClientRect();
      return ra.top - rb.top || ra.left - rb.left;
    });
    batch.forEach((el, i) => reveal(el, Math.min(i * STEP_MS, MAX_DELAY_MS)));
  }

  function scheduleFlush() {
    if (flushScheduled) return;
    flushScheduled = true;
    requestAnimationFrame(flush);
  }

  /** @param {IntersectionObserverEntry[]} entries */
  function onIntersect(entries) {
    for (const entry of entries) {
      if (!pending.has(entry.target)) continue;
      if (entry.isIntersecting) {
        queue.push(entry.target);
      } else if (entry.boundingClientRect.bottom <= 0) {
        showNow(entry.target); // already scrolled past
      }
    }
    if (queue.length) scheduleFlush();
  }

  /**
   * Safety sweep: the -7% margin means something sitting in the last few pixels of the page can never reach the
   * observer's trigger line. Anything already on screen gets revealed.
   */
  function sweep() {
    const height = window.innerHeight;
    for (const el of [...pending]) {
      const rect = el.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) continue; // not displayed (yet)
      if (rect.bottom <= 0) showNow(el);
      else if (rect.top < height) queue.push(el);
    }
    if (queue.length) scheduleFlush();
  }

  let sweepTimer = 0;
  function scheduleSweep(delay = 180) {
    clearTimeout(sweepTimer);
    sweepTimer = setTimeout(sweep, delay);
  }

  function register(el, instant) {
    if (seen.has(el)) return;
    seen.add(el);
    if (instant) {
      pending.add(el);
      showNow(el);
    } else {
      pending.add(el);
      observer.observe(el);
    }
  }

  // Initial page: observe everything that's there.
  collect(document).forEach((el) => register(el, false));

  // Anything added later is shown straight away (it never gets a chance to sit hidden).
  const main = document.querySelector('main') || document.body;
  const mutations = new MutationObserver((records) => {
    for (const record of records) {
      record.addedNodes.forEach((node) => {
        if (node instanceof Element) collect(node).forEach((el) => register(el, true));
      });
    }
  });
  mutations.observe(main, { childList: true, subtree: true });

  // Scroll events don't bubble, so listen in the capture phase to also catch inner scroll containers
  // (on desktop the theme scrolls inside .page-wrapper, not the window).
  document.addEventListener('scroll', () => scheduleSweep(200), { capture: true, passive: true });
  window.addEventListener('resize', () => scheduleSweep(200), { passive: true });
  window.addEventListener('load', () => scheduleSweep(600));
  window.addEventListener('pageshow', () => scheduleSweep(50));
})();
